// Profile.jsx ⚙️ CIRFlask — Avatar Upload + MFA Modal + Tags + Password + Toasts [FINAL FIXED]
import React, { useRef, useState, useEffect } from "react";
import { Toast, ToastContainer } from "react-bootstrap";
import EnableMFAModal from "./EnableMFAModal";
import ConfirmModal from "./ConfirmModal";
import TagInput from "./TagInput";
import "./styles/Profile.css";

export default function Profile({ user, setUser }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [cacheBuster, setCacheBuster] = useState(Date.now());
  const [showMfaModal, setShowMfaModal] = useState(false);
  const [userTags, setUserTags] = useState([]);
  const [toast, setToast] = useState({ show: false, message: "", variant: "success" });
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwError, setPwError] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const showToast = (message, variant = "success") => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast({ ...toast, show: false }), 3000);
  };
  
  const countryToTwemoji = (code) => {
	  if (!code) return "1f3f3f"; // white flag fallback
	  return [...code.toUpperCase()]
		.map((c) => 127397 + c.charCodeAt())
		.map((cp) => cp.toString(16))
		.join("-");
	};

  const {
    display_name,
    username,
    email,
    phone_number,
    job_title,
    location,
    status,
    created_at,
    last_logged_in,
    mfa_secret,
    avatar_url,
	user_type
  } = user;

  useEffect(() => {
    fetch("/api/get_user_tags.php", { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data.tags)) setUserTags(data.tags);
      })
      .catch(() => console.error("Failed to load user tags"));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => {
      fetch("/api/save_user_tags.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tags: userTags }),
        signal: controller.signal
      })
        .then(res => res.json())
        .then(data => {
          if (!data.success) showToast(data.error || "Tag save failed", "danger");
        })
        .catch(err => {
          if (err.name !== "AbortError") showToast("Network error saving tags", "danger");
        });
    }, 1000);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [userTags]);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("avatar", file);

    setUploading(true);
    setError("");

    try {
      const res = await fetch("/api/upload_avatar.php", {
        method: "POST",
        body: formData,
        credentials: "include"
      });

      const data = await res.json();
      if (data.success) {
        const newUrl = `${data.avatar}?cb=${Date.now()}`;
        const img = new Image();
        img.onload = () => {
          setUser({ ...user, avatar_url: newUrl });
          setCacheBuster(Date.now());
          showToast("Avatar updated", "success");
        };
        img.onerror = () => setError("Failed to preload avatar.");
        img.src = newUrl;
      } else {
        setError(data.error || "Upload failed");
      }
    } catch {
      setError("Network error");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAvatar = async () => {
    try {
      const res = await fetch("/api/delete_avatar.php", {
        method: "POST",
        credentials: "include"
      });
      const data = await res.json();
      if (data.success) {
        setUser({ ...user, avatar_url: "" });
        setCacheBuster(Date.now());
        showToast("Avatar removed", "info");
      } else {
        setError(data.error || "Failed to delete avatar");
      }
    } catch {
      setError("Network error");
    }
  };

	const handleDisableMFA = async () => {
	  try {
		const res = await fetch("/api/mfa_disable.php", {
		  method: "POST",
		  credentials: "include"
		});
		const data = await res.json();
		if (data.success) {
		  setUser({ ...user, mfa_secret: null });
		  showToast("MFA disabled", "info");
		} else {
		  setError(data.error || "Failed to disable MFA");
		}
	  } catch {
		setError("Network error");
	  } finally {
		setShowConfirmModal(false);
	  }
	};

	const handlePasswordChange = async () => {
	  setPwError("");

	  if (!pwCurrent || !pwNew || !pwConfirm) {
		setPwError("All fields are required.");
		return;
	  }

	  if (pwNew !== pwConfirm) {
		setPwError("New passwords do not match.");
		return;
	  }

	  setPwLoading(true);

	  try {
		const res = await fetch("/api/change_password.php", {
		  method: "POST",
		  credentials: "include",
		  headers: { "Content-Type": "application/json" },
		  body: JSON.stringify({
			current_password: pwCurrent,
			new_password: pwNew
		  })
		});

		const data = await res.json();

		if (data.success) {
		  setPwCurrent("");
		  setPwNew("");
		  setPwConfirm("");
		  showToast("Password updated", "success");
		} else {
		  setPwError(data.error || "Failed to update password.");
		}
	  } catch {
		setPwError("Network error.");
	  } finally {
		setPwLoading(false);
	  }
	};

	return (
	  <div className="profile-wrapper">
		<div className="profile-section">
		<button
		  className="edit-profile-btn"
		  data-bs-toggle="tooltip"
		  data-bs-placement="top"
		  title="Edit Profile"
		  onClick={() => console.log("Edit mode coming soon...")}
		>
		  <i className="bi bi-pencil-fill"></i>
		</button>
		  <div className="profile-avatar-container">
			  <div className="avatar-clickable" onClick={() => fileInputRef.current?.click()}>
				<img
				  key={`${avatar_url}-${cacheBuster}`}
				  src={avatar_url || "/img/avatar_default.jpg"}
				  alt="Avatar"
				  className="profile-avatar-img"
				/>
				<div className="avatar-overlay">
				  <i className="bi bi-camera-fill avatar-camera-icon"></i>
				</div>
			  </div>
			  <input
				type="file"
				accept="image/png, image/jpeg"
				ref={fileInputRef}
				style={{ display: "none" }}
				onChange={handleAvatarUpload}
			  />
			  <div className="profile-name">{display_name || username}</div>
			  <div className="profile-role">{job_title || "User"}</div>
			  {avatar_url && (
				<button
				  className="btn-profile btn-danger mt-3"
				  style={{ padding: "4px 12px", fontSize: "0.8rem" }}
				  onClick={(e) => {
					e.stopPropagation();
					handleDeleteAvatar();
				  }}
				>
				  Delete Avatar
				</button>
			  )}
			</div>

		  <div className="profile-info">
			<div className="info-row"><span>Username</span><span>{username}</span></div>
			<div className="info-row"><span>Email</span><span>{email}</span></div>
			<div className="info-row"><span>Phone</span><span>{phone_number || "—"}</span></div>
			<div className="info-row">
			  <span>Location</span>
			  <span>
				{location ? (
				  <div
					  className="flag-circle-zoom"
					  title={location.toUpperCase()}
					  style={{
						backgroundImage: `url(https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/svg/${countryToTwemoji(location)}.svg)`
					  }}
					></div>
				) : (
				  "—"
				)}
			  </span>
			</div>
			<div className="info-row"><span>Role</span><span className="role-badge">{user_type || "User"}</span></div>
			<div className="info-row mfa-row">
			  <span>MFA</span>
			  <div className="mfa-status-wrapper">
				<span className={`profile-status-badge ${mfa_secret ? "active" : "inactive"}`}>
				  {mfa_secret ? "Enabled" : "Disabled"}
				</span>
				<button
				  className={`btn-profile btn-sm ${mfa_secret ? "btn-danger" : "btn-success"}`}
				  onClick={() => (mfa_secret ? setShowConfirmModal(true) : setShowMfaModal(true))}
				>
				  {mfa_secret ? "Disable" : "Enable"}
				</button>
			  </div>
			</div>
			<div className="info-row"><span>Joined</span><span>{new Date(created_at).toLocaleString()}</span></div>
			<div className="info-row"><span>Last Login</span><span>{new Date(last_logged_in).toLocaleString()}</span></div>
		  </div>

		  <div
			className="mt-1"
			style={{
			  background: "#1e1e1e",
			  border: "1px solid #2b2b2b",
			  borderRadius: "8px",
			  padding: "1rem",
			  marginTop: "2rem",
			}}
		  >
			<strong style={{ color: "#bbb", fontSize: "0.9rem" }}>Tags</strong>
			<TagInput tags={userTags} setTags={setUserTags} placeholder="Add tags..." />
		  </div>

			{/* 🔐 Change Password Section */}
			<div
			  className="password-block mt-1"
			  style={{
				background: "#1e1e1e",
				border: "1px solid #2b2b2b",
				borderRadius: "8px",
				padding: "1rem",
				marginTop: "2rem",
			  }}
			>
			  <strong style={{ color: "#bbb", fontSize: "0.9rem" }}>Change Password</strong>

			  <div className="profile-info mt-3">
				<div className="info-row">
				  <span>Current</span>
				  <input
					type="password"
					className="form-control form-control-sm bg-dark text-light border-0"
					value={pwCurrent}
					onChange={(e) => setPwCurrent(e.target.value)}
					placeholder="••••••"
				  />
				</div>
				<div className="info-row">
				  <span>New</span>
				  <input
					type="password"
					className="form-control form-control-sm bg-dark text-light border-0"
					value={pwNew}
					onChange={(e) => setPwNew(e.target.value)}
					placeholder="••••••"
				  />
				</div>
				<div className="info-row">
				  <span>Confirm</span>
				  <input
					type="password"
					className="form-control form-control-sm bg-dark text-light border-0"
					value={pwConfirm}
					onChange={(e) => setPwConfirm(e.target.value)}
					placeholder="••••••"
				  />
				</div>
			  </div>

			  <div className="text-end mt-3">
				<button
				  className="btn-profile btn-success"
				  style={{ padding: "6px 20px", fontSize: "0.85rem" }}
				  onClick={handlePasswordChange}
				  disabled={pwLoading}
				>
				  {pwLoading ? "Updating..." : "Update"}
				</button>
				{pwError && <div className="text-danger mt-2 small">{pwError}</div>}
			  </div>
			</div>

		  {error && <div className="alert alert-danger mt-3 text-center py-2">{error}</div>}
		</div>

		<ToastContainer position="bottom-end" className="p-3" style={{ position: "fixed", bottom: "20px", right: "20px", zIndex: 9999 }}>
		  <Toast show={toast.show} bg={toast.variant} onClose={() => setToast({ ...toast, show: false })} delay={3000} autohide>
			<Toast.Body className="text-white">{toast.message}</Toast.Body>
		  </Toast>
		</ToastContainer>

		<EnableMFAModal
		  show={showMfaModal}
		  onHide={() => setShowMfaModal(false)}
		  setUser={setUser}
		  onSuccess={() => showToast("MFA enabled", "success")}
		/>
		<ConfirmModal
		  show={showConfirmModal}
		  onCancel={() => setShowConfirmModal(false)}
		  onConfirm={handleDisableMFA}
		  title="Disable MFA?"
		  message="This will turn off multi-factor authentication for your account. Proceed?"
		/>
	  </div>
	);
}
